import { Alert, AlertTitle, Chip, IconButton, Typography } from "@mui/material";
import {
  BaseSuggestionData,
  MentionsTextField,
  MentionsTextFieldProps,
} from "@jackstenglein/mui-mentions";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOff from "@mui/icons-material/MicOff";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { LoadingAnimation } from "./LoadingAnimation";
import { useAppState } from "../../state/App";
import {
  FilesPreview,
  FileUploadButton,
  FileUploadProps,
} from "../common/FilesSelector";
import { useModeName, useModes } from "../../hooks/useModes";
import { useReactMediaRecorder } from "react-media-recorder";
import CircularProgress from "@mui/material/CircularProgress";
import { defineMessages, useIntl } from "react-intl";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useSelectedMode } from "../../hooks/useSelectedMode";

const mentionedRegex = /@[^\s]*$/;

const messages = defineMessages({
  recordingTitle: {
    id: "recording.title",
    defaultMessage: "Recording in progress",
  },
  recordingText: {
    id: "recording.text",
    defaultMessage: "Stop the recording to submit your question.",
  },
  examplePrompts: {
    id: "chat.examplePrompts",
    defaultMessage: "Example prompts",
  },
});

// Workaround to deal with MentionsTextField returning a ReactNode
function TextField<T extends BaseSuggestionData>(
  props: MentionsTextFieldProps<T>
) {
  return (
    <>
      {typeof MentionsTextField === "undefined"
        ? null
        : MentionsTextField(props)}
    </>
  );
}

type QuestionInputProps = {
  onSend: (args: {
    modeId?: string;
    message?: string;
    files?: File[];
    recording?: Blob;
    sessionId?: string;
  }) => void;
  disabled: boolean;
  placeholder?: string;
  sessionId?: string;
  loading?: boolean;
  fileUploadProps: FileUploadProps;
};

export type QuestionInputHandle = {
  clearInput: () => void;
};

export const QuestionInput = forwardRef<
  QuestionInputHandle,
  QuestionInputProps
>(
  (
    {
      onSend,
      disabled,
      placeholder,
      sessionId,
      loading = false,
      fileUploadProps: { previewProps, buttonProps },
    },
    ref
  ) => {
    const { formatMessage: t } = useIntl();
    const {
      state: { question, isPlaying, isGenerating },
      actions: { setQuestion, setCurrentTTSUrl, setIsPlaying },
    } = useAppState();

    const [visibleQuestion, setVisibleQuestion] = useState(question);
    const [strippedQuestion, setStrippedQuestion] = useState(question);
    const [mentionedModeId, setMentionedModeId] = useState<string | undefined>(
      undefined
    );

    const spellCheckPlainTextRef = useRef<string | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        clearInput: () => {
          // Explicitly reset local state. The useEffect below would do this
          // too if `question` changed, but `question` in global state is not
          // kept in sync with keystrokes (to avoid per-keystroke re-renders),
          // so it may already be "" and the effect would not re-run.
          // setQuestion("") keeps global state consistent for the regenerate
          // flow in Question.tsx which uses it to pre-populate the input.
          setQuestion("");
          setVisibleQuestion("");
          setStrippedQuestion("");
          setMentionedModeId(undefined);
          buttonProps.clear();
        },
      }),
      [buttonProps, setQuestion]
    );

    const { data: modes, isLoading: isModesLoading } = useModes();
    const getModeName = useModeName();
    const { data: selectedMode } = useSelectedMode();

    const { status, error, startRecording, stopRecording } =
      useReactMediaRecorder({
        audio: true,
        mediaRecorderOptions: {
          mimeType: "audio/wav",
        },
        onStop: (_, recording: Blob) => {
          setCurrentTTSUrl(undefined);
          setIsPlaying(false);
          buttonProps.clear();
          onSend({ recording, sessionId, files: previewProps.files });
        },
      });

    useEffect(() => {
      setVisibleQuestion(question);
      setStrippedQuestion("");
      setMentionedModeId(undefined);
    }, [question]);

    const { text: previewText, setText: setPreviewText } = previewProps;
    useEffect(() => {
      if (previewText) {
        setVisibleQuestion((old) =>
          old ? `${old}${previewText}` : previewText
        );
        setStrippedQuestion("");
        setPreviewText?.(undefined);
      }
    }, [previewText, setPreviewText]);

    const sendQuestionDisabled = loading || disabled || isModesLoading;

    const sendQuestion = (prompt?: string) => {
      const message = prompt || strippedQuestion || visibleQuestion || "";

      if (sendQuestionDisabled || message.trim() === "") {
        return;
      }

      if (sessionId) {
        onSend({
          modeId: mentionedModeId,
          message: message,
          files: previewProps.files,
          sessionId,
        });
      } else {
        onSend({
          modeId: mentionedModeId,
          message: message,
          files: previewProps.files,
        });
      }
    };

    // Unfornately, the MentionsTextField component does not provide a way to
    // distinguish in the `onKeyDown`-handler whether the user is pressing Enter
    // while accepting a mention suggestion or not. As a workaround, we check
    // the last word in the input and if it is a mention, do not send the question.
    const onEnterPress = (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        ev.key === "Enter" &&
        !ev.shiftKey &&
        ev.currentTarget.value.trim() !== "" &&
        !mentionedRegex.test(ev.currentTarget.value)
      ) {
        ev.preventDefault();
        sendQuestion();
      }
    };

    const hasMicPermission = !(
      status === "permission_denied" || error === "permission_denied"
    );
    const isAcquiringMedia = status === "acquiring_media";
    const isRecording = status === "recording";
    const promptExamples =
      selectedMode?.prompt_examples?.map((prompt) => prompt) || [];
    const showPrompExamples =
      !sessionId && // new chat
      promptExamples.length > 0 && // has prompt examples
      previewProps.files.length === 0 && // no files
      !isRecording && // not recording audio
      !isGenerating; // not generating

    return (
      <div className="w-full flex flex-col">
        <div className="relative">
          {isRecording && (
            <Alert icon={<GraphicEqIcon fontSize="inherit" />} severity="info">
              <AlertTitle>{t(messages.recordingTitle)}</AlertTitle>
              {t(messages.recordingText)}
            </Alert>
          )}
          {showPrompExamples && (
            <div className="text-center">
              <Typography className="mb-2">
                {t(messages.examplePrompts)}
              </Typography>
              {promptExamples.map((prompt, idx) => (
                <Chip
                  key={idx}
                  label={prompt}
                  clickable
                  variant="outlined"
                  className="mr-4 mb-2"
                  onClick={() => {
                    sendQuestion(prompt);
                  }}
                />
              ))}
            </div>
          )}
          <div className="w-full flex flex-wrap absolute bottom-0">
            <div className="w-full flex justify-end">
              {!loading && <FilesPreview {...previewProps} />}
            </div>
          </div>
        </div>
        <div className="w-full flex align-middle">
          <TextField
            dataSources={[
              {
                data: mentionedModeId
                  ? []
                  : modes?.map((mode) => ({
                      id: mode._id,
                      display: getModeName(mode._id),
                    })) || [],
                displayTransform: (id) => `@${getModeName(id)}`,
                appendSpaceOnAdd: true,
              },
            ]}
            autoFocus
            fullWidth
            placeholder={placeholder}
            disabled={sendQuestionDisabled}
            multiline
            minRows={1}
            maxRows={10}
            value={visibleQuestion}
            onChange={(v, o, m) => {
              // If spell check just happened, use the stored plain text to
              // - reconstruct the visibleQuestion (with markup)
              // - reconstruct the strippedQuestion (without markup) accordingly
              if (spellCheckPlainTextRef.current !== null) {
                const correctedPlainText = spellCheckPlainTextRef.current;
                spellCheckPlainTextRef.current = null;

                // Reconstruct v by replacing mention displays in correctedPlainText with markup
                let reconstructedV = correctedPlainText;
                m.forEach(({ display, id }) => {
                  // Build the mention markup format used by MentionsTextField
                  const mentionMarkup = `@[${display}](${id})`;

                  // Find the display in the corrected text and check if markup exists in original
                  if (o.includes(display)) {
                    // Replace the plain text display with the markup
                    reconstructedV = reconstructedV.replace(
                      display,
                      mentionMarkup
                    );
                  }
                });

                let stripped = correctedPlainText;
                m.forEach(({ dataSourceIndex, display }) => {
                  if (dataSourceIndex === 0) {
                    stripped = stripped.replace(display, "").trim();
                  }
                });
                setStrippedQuestion(stripped);
                setMentionedModeId(m[0]?.id);
                setVisibleQuestion(reconstructedV);
                return;
              }

              // Normal onChange handling
              let stripped = o;
              // remove mentions from the question
              m.forEach(({ dataSourceIndex, display }) => {
                if (dataSourceIndex === 0) {
                  stripped = stripped.replace(display, "").trim();
                }
              });
              setStrippedQuestion(stripped);
              setMentionedModeId(m[0]?.id);
              setVisibleQuestion(v);
            }}
            InputProps={{
              onInput: (e) => {
                const inputEvent = e.nativeEvent as InputEvent;
                if (inputEvent.inputType === "insertReplacementText") {
                  // Spell check replacement - store the corrected plain text
                  const correctedPlainText = (e.target as HTMLInputElement)
                    .value;
                  spellCheckPlainTextRef.current = correctedPlainText.trim();
                }
              },
              onKeyDown: onEnterPress,
              startAdornment: (
                <div className="flex">
                  <FileUploadButton
                    id="question-input-attachment"
                    {...buttonProps}
                  />
                </div>
              ),
              endAdornment: (
                <>
                  {visibleQuestion.length === 0 ? (
                    hasMicPermission ? (
                      <IconButton
                        disabled={loading}
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          isPlaying
                            ? setIsPlaying(false)
                            : isRecording
                            ? stopRecording()
                            : startRecording();
                        }}
                      >
                        {isAcquiringMedia ? (
                          <CircularProgress size={24} />
                        ) : isRecording || isPlaying ? (
                          <StopCircleIcon color="primary" />
                        ) : loading ? (
                          <LoadingAnimation />
                        ) : (
                          <MicIcon color="primary" />
                        )}
                      </IconButton>
                    ) : (
                      <IconButton disabled={!hasMicPermission}>
                        <MicOff color="disabled" />
                      </IconButton>
                    )
                  ) : null}
                  {visibleQuestion.length > 0 && !isRecording && (
                    <IconButton
                      disabled={sendQuestionDisabled}
                      tabIndex={0}
                      onClick={() => sendQuestion()}
                      onKeyDown={(e) =>
                        e.key === "Enter" || e.key === " "
                          ? sendQuestion()
                          : null
                      }
                    >
                      {loading ? (
                        <LoadingAnimation />
                      ) : (
                        <SendIcon color="primary" />
                      )}
                    </IconButton>
                  )}
                </>
              ),
            }}
          />
        </div>
      </div>
    );
  }
);

QuestionInput.displayName = "QuestionInput";
